import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, where, onSnapshot, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db, analytics } from '../firebase';
import type { Lobby, Player, WhiteCard, BlackCard, CardCategory, Submission, GameSettings } from '../../types/game';
import { logEvent } from 'firebase/analytics';

// Exporte der Typen, damit andere Module sie von hier importieren können
export type { Lobby, Player, WhiteCard, BlackCard, CardCategory };

// Collection Names
export const COLLECTIONS = {
  CARD_CATEGORIES: 'cardCategories',
  BLACK_CARDS: 'blackCards',
  WHITE_CARDS: 'whiteCards',
  LOBBIES: 'lobbies',
} as const;

// Types
/*
  ENTFERNT: Die lokalen Typen wurden entfernt und werden jetzt aus /types/game.ts importiert,
  um eine einzige Quelle der Wahrheit für die Datenmodelle zu haben.
*/

// Collection References
export const cardCategoriesRef = collection(db, COLLECTIONS.CARD_CATEGORIES);
export const blackCardsRef = collection(db, COLLECTIONS.BLACK_CARDS);
export const whiteCardsRef = collection(db, COLLECTIONS.WHITE_CARDS);
export const lobbiesRef = collection(db, COLLECTIONS.LOBBIES);

// Lobby Funktionen
export async function getActiveLobbies() {
  const lobbiesRef = collection(db, 'lobbies');
  const q = query(lobbiesRef, where('status', '==', 'waiting'));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Lobby[];
}

// Hilfsfunktion zum Entfernen von undefined-Feldern
function removeUndefinedFields<T extends object>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => v !== undefined)
  ) as T;
}

export const createLobby = async (hostName: string, hostUid: string, selectedCategoryId: string, gameSettings: GameSettings) => {
  try {
    const hostPlayer: Player = {
      uid: hostUid,
      name: hostName,
      score: 0,
      hand: [],
      isCzar: false,
    };

    // undefined-Felder aus gameSettings entfernen
    const cleanedGameSettings = removeUndefinedFields(gameSettings);

    const newLobby: Omit<Lobby, 'id'> = {
      hostName,
      hostUid,
      createdAt: new Date(),
      lastActivity: new Date(),
      gameState: 'waiting',
      roundNumber: 0,
      selectedCategoryId,
      players: [hostPlayer],
      playerUids: [hostUid],
      submissions: [],
      availableWhiteCards: [],
      availableBlackCards: [],
      discardPileWhite: [],
      discardPileBlack: [],
      gameSettings: cleanedGameSettings,
    };
    
    const lobbyRef = await addDoc(collection(db, COLLECTIONS.LOBBIES), newLobby);
    
    logEvent(analytics, 'create_lobby', {
      lobby_id: lobbyRef.id,
      category_id: selectedCategoryId,
      host_name: hostName,
    });

    return lobbyRef.id;
  } catch (error) {
    console.error("Fehler beim Erstellen der Lobby: ", error);
    throw error;
  }
};

export const updateLobby = async (lobbyId: string, data: Partial<Lobby>) => {
  const lobbyRef = doc(db, COLLECTIONS.LOBBIES, lobbyId);
  await updateDoc(lobbyRef, { ...data, lastActivity: new Date() });
};

export const deleteLobby = async (lobbyId: string) => {
  const lobbyRef = doc(db, 'lobbies', lobbyId);
  return await deleteDoc(lobbyRef);
};

export async function getLobbies() {
  const lobbiesRef = collection(db, 'lobbies');
  const snapshot = await getDocs(lobbiesRef);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Lobby[];
}

export async function getLobby(lobbyId: string) {
  const lobbyRef = doc(db, 'lobbies', lobbyId);
  const lobbyDoc = await getDoc(lobbyRef);
  
  if (!lobbyDoc.exists()) {
    return null;
  }

  return {
    id: lobbyDoc.id,
    ...lobbyDoc.data()
  } as Lobby;
}

export function onLobbyUpdate(
  lobbyId: string,
  callback: (lobby: Lobby | null) => void,
  onError: (error: Error) => void
) {
  const lobbyDocRef = doc(db, COLLECTIONS.LOBBIES, lobbyId);
  return onSnapshot(lobbyDocRef, (doc) => {
    if (doc.exists()) {
      callback({ id: doc.id, ...doc.data() } as Lobby);
    } else {
      callback(null);
    }
  }, onError);
}

export function onLobbiesUpdate(callback: (lobbies: Lobby[]) => void, onError: (error: Error) => void) {
  const lobbiesCollectionRef = collection(db, COLLECTIONS.LOBBIES);
  const q = query(lobbiesCollectionRef, where("gameState", "!=", "ended"));

  return onSnapshot(q, (querySnapshot) => {
    const lobbies = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Lobby));
    callback(lobbies);
  }, onError);
}

export const joinLobby = async (lobbyId: string, player: Player) => {
  try {
    const lobbyRef = doc(db, 'lobbies', lobbyId);
  await updateDoc(lobbyRef, {
      players: arrayUnion(player),
      playerUids: arrayUnion(player.uid),
      lastActivity: new Date(),
  });
    logEvent(analytics, 'join_lobby', { lobby_id: lobbyId, player_name: player.name });
  } catch (error) {
    console.error("Fehler beim Beitreten der Lobby:", error);
    throw error;
}
};

export async function leaveLobby(lobbyId: string, playerId: string) {
  const lobbyRef = doc(db, 'lobbies', lobbyId);
  const lobbyDoc = await getDoc(lobbyRef);
  
  if (!lobbyDoc.exists()) {
    throw new Error('Lobby nicht gefunden');
  }

  const lobby = lobbyDoc.data() as Lobby;
  const playerToRemove = lobby.players.find(p => p.uid === playerId);

  if (!playerToRemove) {
    console.warn("Spieler nicht in der Lobby gefunden, kann ihn nicht entfernen.");
    return;
  }

  await updateDoc(lobbyRef, {
    players: arrayRemove(playerToRemove),
    playerUids: arrayRemove(playerId)
  });
}

// Cards Collection
const getCards = async <T extends BlackCard | WhiteCard>(collectionName: string, categoryId: string): Promise<T[]> => {
  try {
    const cardsRef = collection(db, collectionName);
    const q = query(cardsRef, where('categoryIds', 'array-contains', categoryId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.warn(`Keine Karten im Typ "${collectionName}" für Kategorie "${categoryId}" gefunden.`);
      return [];
    }
    
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as T));
  } catch (error) {
    console.error(`Fehler beim Laden der Karten aus ${collectionName}:`, error);
    throw error;
  }
};

export const importCards = async (cards: any) => {
  const cardsRef = collection(db, 'cards');
  const batch = [];
  
  for (const card of cards) {
    batch.push(addDoc(cardsRef, {
      ...card,
      createdAt: new Date().toISOString()
    }));
  }
  
  return await Promise.all(batch);
};

// Game Collection
export const createGame = async (gameData: any) => {
  const gamesRef = collection(db, 'games');
  return await addDoc(gamesRef, {
    ...gameData,
    status: 'active',
    createdAt: new Date().toISOString()
  });
};

export const updateGame = async (gameId: string, data: any) => {
  const gameRef = doc(db, 'games', gameId);
  return await updateDoc(gameRef, data);
};

export const getActiveGames = async () => {
  const gamesRef = collection(db, 'games');
  const q = query(gamesRef, where('status', '==', 'active'));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}; 

export const startGame = async (lobbyId: string) => {
  try {
    const lobbyRef = doc(db, 'lobbies', lobbyId);
    const lobby = await getLobby(lobbyId);
    if (!lobby) throw new Error("Lobby nicht gefunden");

    // 1. Alle Karten (schwarz und weiß) aus der ausgewählten Kategorie laden
    const allBlackCards = await getCards<BlackCard>(COLLECTIONS.BLACK_CARDS, lobby.selectedCategoryId);
    const allWhiteCards = await getCards<WhiteCard>(COLLECTIONS.WHITE_CARDS, lobby.selectedCategoryId);

    // 1a. Prüfen, ob genügend Karten vorhanden sind
    const handSize = lobby.gameSettings?.handSize || 7;
    if (allBlackCards.length < 1) {
      throw new Error("In dieser Kategorie gibt es keine schwarzen Karten, um das Spiel zu starten.");
    }
    if (allWhiteCards.length < lobby.players.length * handSize) {
      throw new Error("Nicht genügend weiße Karten für diese Spieleranzahl in der Kategorie.");
    }
    
    // 2. Karten mischen
    const shuffledBlackCards = fisherYatesShuffle(allBlackCards);
    let cardsToDeal = fisherYatesShuffle(allWhiteCards);

    // 4. Spieler mit Karten ausstatten
    const playersWithHands = lobby.players.map(player => {
      const hand = cardsToDeal.slice(0, handSize);
      cardsToDeal = cardsToDeal.slice(handSize); // Karten aus dem Stapel entfernen
      return { ...player, hand };
    });
    console.log(`Spiel erfolgreich gestartet! Handgröße: ${handSize} Karten pro Spieler`);

    // 5. Einen zufälligen Card Czar bestimmen
    const czarIndex = Math.floor(Math.random() * playersWithHands.length);
    const playersWithCzar = playersWithHands.map((player, index) => ({
      ...player,
      isCzar: index === czarIndex,
    }));

    // 6. Eine zufällige schwarze Karte auswählen
    const randomIndex = Math.floor(Math.random() * shuffledBlackCards.length);
    const firstBlackCard = shuffledBlackCards[randomIndex];

    // 7. Verwendete schwarze Karte aus dem Stapel entfernen
    const remainingBlackCards = shuffledBlackCards.filter(card => card.id !== firstBlackCard.id);

    // 8. Runden-Endzeit berechnen
    const roundTime = lobby.gameSettings?.roundTimeSeconds || 90;
    const roundEndTime = lobby.gameSettings?.timerEnabled 
      ? new Date(Date.now() + roundTime * 1000) 
      : undefined;

    // 9. Lobby updaten
    await updateDoc(lobbyRef, {
      players: playersWithCzar,
      gameState: 'playing',
      gamePhase: 'selecting',
      roundEndTime: roundEndTime,
      currentBlackCard: firstBlackCard,
      currentCzarUid: playersWithCzar[czarIndex].uid,
      availableBlackCards: remainingBlackCards,
      availableWhiteCards: cardsToDeal, // Korrekter Stapel übriger Karten
      discardPileBlack: [firstBlackCard],
      discardPileWhite: [],
      roundNumber: 1,
    });
    logEvent(analytics, 'start_game', { lobby_id: lobbyId });
  } catch (error) {
    console.error("Fehler beim Starten des Spiels:", error);
    // Hier wäre es gut, dem Host eine Fehlermeldung anzuzeigen
    throw error;
  }
};

// Beispielkarten importieren
export const importSampleCards = async () => {
  try {
    // Beispiel schwarze Karten
    const sampleBlackCards = [
      {
        text: "Warum kann ich nicht schlafen?",
        pick: 1,
        categoryIds: ["default"],
        createdAt: new Date(),
        author: "System"
      },
      {
        text: "Was ist das Beste an _ ?",
        pick: 1,
        categoryIds: ["default"],
        createdAt: new Date(),
        author: "System"
      },
      {
        text: "_ + _ = Ein perfekter Abend.",
        pick: 2,
        categoryIds: ["default"],
        createdAt: new Date(),
        author: "System"
      },
      {
        text: "Meine Oma ist schockiert von _.",
        pick: 1,
        categoryIds: ["default"],
        createdAt: new Date(),
        author: "System"
      },
      {
        text: "Was bringt mich immer zum Lachen?",
        pick: 1,
        categoryIds: ["default"],
        createdAt: new Date(),
        author: "System"
      }
    ];

    // Beispiel weiße Karten
    const sampleWhiteCards = [
      { text: "Katzenvideos", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Schlechte Witze", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Pizza um 3 Uhr morgens", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Meine Socken", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Ein Einhorn", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Mein Lieblingslied", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Schokolade", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Mein Handy", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Regentage", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Meine Freunde", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Kaffee", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Mein Bett", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Videospiele", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Meine Mutter", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Das Internet", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Mein Lieblingsbuch", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Urlaub", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Mein Auto", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Musik", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Mein Haustier", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Filme", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Mein Hobby", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Wochenende", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Mein Lieblingswetter", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Feiertage", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Mein Lieblingssport", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Mein Lieblingessen", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Mein Lieblingsort", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Meine Lieblingsserie", categoryIds: ["default"], createdAt: new Date(), author: "System" },
      { text: "Mein Lieblingsspiel", categoryIds: ["default"], createdAt: new Date(), author: "System" }
    ];

    // Beispiel Kategorie
    const sampleCategory = {
      name: "Standard",
      description: "Grundlegende Karten für alle Altersgruppen",
      isActive: true,
      createdAt: new Date(),
      iconUrl: ""
    };

    // Kategorie hinzufügen
    const categoryDoc = await addDoc(cardCategoriesRef, sampleCategory);
    const categoryId = categoryDoc.id;

    // Karten mit der richtigen Kategorie-ID hinzufügen
    const blackCardsWithCategory = sampleBlackCards.map(card => ({
      ...card,
      categoryIds: [categoryId]
    }));

    const whiteCardsWithCategory = sampleWhiteCards.map(card => ({
      ...card,
      categoryIds: [categoryId]
    }));

    // Karten in Firestore hinzufügen
    const blackCardPromises = blackCardsWithCategory.map(card => addDoc(blackCardsRef, card));
    const whiteCardPromises = whiteCardsWithCategory.map(card => addDoc(whiteCardsRef, card));

    await Promise.all([...blackCardPromises, ...whiteCardPromises]);

    console.log('Beispielkarten erfolgreich importiert!');
    console.log(`Kategorie ID: ${categoryId}`);
    
    return categoryId;
  } catch (error) {
    console.error('Fehler beim Importieren der Beispielkarten:', error);
    throw error;
  }
};

// Karten aus dystopie.json importieren
export const importDystopieCards = async () => {
  try {
    console.log('Importiere Karten aus dystopie.json...');
    
    // Lade die dystopie.json Datei
    const response = await fetch('/dystopie.json');
    const data = await response.json();
    
    // Erstelle oder finde die Dystopie-Kategorie
    const categoryData = {
      name: 'Dystopie',
      description: 'Dunkle und dystopische Karten für Erwachsene',
      isActive: true,
      createdAt: new Date(),
      iconUrl: ''
    };
    
    const categoryDoc = await addDoc(cardCategoriesRef, categoryData);
    const categoryId = categoryDoc.id;
    console.log('Kategorie erstellt:', categoryId);

    // Importiere schwarze Karten
    const blackCardsToImport = data.black_cards.map((card: any) => ({
      text: card.text,
      pick: card.pick,
      categoryIds: [categoryId],
      createdAt: new Date(),
      author: 'Dystopie'
    }));

    // Importiere weiße Karten
    const whiteCardsToImport = data.white_cards.map((text: string) => ({
      text: text,
      categoryIds: [categoryId],
      createdAt: new Date(),
      author: 'Dystopie'
    }));

    // Batch-Import für bessere Performance
    const blackCardPromises = blackCardsToImport.map((card: any) => addDoc(blackCardsRef, card));
    const whiteCardPromises = whiteCardsToImport.map((card: any) => addDoc(whiteCardsRef, card));

    await Promise.all([...blackCardPromises, ...whiteCardPromises]);

    console.log('Importiert: ' + blackCardsToImport.length + ' schwarze Karten, ' + whiteCardsToImport.length + ' weiße Karten');
    
    return {
      categoryId,
      blackCards: blackCardsToImport.length,
      whiteCards: whiteCardsToImport.length
    };
  } catch (error) {
    console.error('Fehler beim Importieren der Karten:', error);
    throw error;
  }
};

// Karten einreichen
export const submitCards = async (lobbyId: string, playerUid: string, cardIds: string[]) => {
  try {
    const lobbyRef = doc(db, 'lobbies', lobbyId);
    const lobby = await getLobby(lobbyId);
    
    if (!lobby) {
      throw new Error('Lobby nicht gefunden');
    }

    const player = lobby.players.find(p => p.uid === playerUid);
    if (!player) {
      throw new Error('Spieler nicht gefunden.');
    }

    const hasSubmitted = (lobby.submissions || []).some(s => s.playerUid === playerUid);
    if (hasSubmitted) {
      throw new Error('Du hast bereits Karten eingereicht');
    }

    const submittedCards = player.hand.filter(card => cardIds.includes(card.id));
    if (submittedCards.length !== cardIds.length) {
      throw new Error("Einige der ausgewählten Karten wurden nicht in der Hand des Spielers gefunden.");
    }

    const submission: Omit<Submission, 'timestamp'> = {
      playerUid,
      cards: submittedCards,
      votes: 0,
    };

    const updatedSubmissions = [...lobby.submissions, { ...submission, timestamp: new Date() }];

    // Entferne die eingereichten Karten aus der Hand des Spielers
    const updatedPlayers = lobby.players.map(p => {
      if (p.uid === playerUid) {
        return {
          ...p,
          hand: p.hand.filter(card => !cardIds.includes(card.id))
        };
      }
      return p;
    });

    const allPlayersSubmitted = lobby.players
      .filter(p => !p.isCzar)
      .every(p => updatedSubmissions.some(s => s.playerUid === p.uid));

    const updateData: Partial<Lobby> = {
      submissions: updatedSubmissions,
      players: updatedPlayers,  // Aktualisierte Spieler mit entfernten Karten
    };

    if (allPlayersSubmitted) {
      updateData.gamePhase = 'judging';
    }

    await updateDoc(lobbyRef, updateData);
  } catch (error) {
    console.error('Fehler beim Einreichen der Karten:', error);
    throw error;
  }
};

export const revealSubmission = async (lobbyId: string, submissionIndex: number) => {
  try {
    const lobbyRef = doc(db, 'lobbies', lobbyId);
    await updateDoc(lobbyRef, { 
      revealedSubmissionIndices: arrayUnion(submissionIndex) 
    });
  } catch (error) {
    console.error("Fehler beim Aufdecken der Karte:", error);
    throw error;
  }
};

// Gewinner auswählen (nur Card Czar)
export const selectWinner = async (lobbyId: string, winnerUid: string) => {
  try {
    const lobbyRef = doc(db, 'lobbies', lobbyId);
    const lobby = await getLobby(lobbyId);

    if (!lobby) throw new Error("Lobby nicht gefunden");

    const winningSubmission = (lobby.submissions || []).find(s => s.playerUid === winnerUid);
    if (!winningSubmission) throw new Error("Gewinner-Einreichung nicht gefunden.");

    let winnerOfGame = false;
    const winningScore = lobby.gameSettings?.winningScore || 10;

    const updatedPlayers = lobby.players.map(p => {
      if (p.uid === winnerUid) {
        const newScore = p.score + 1;
        if (newScore >= winningScore) {
          winnerOfGame = true;
        }
        return { ...p, score: newScore };
      }
      return p;
    });
    
    const winnerPlayer = updatedPlayers.find(p => p.uid === winnerUid);
    if (!winnerPlayer) throw new Error("Gewinner-Spieler nicht gefunden nach Update.");

    const updateData: Partial<Lobby> = {
      players: updatedPlayers,
      gamePhase: 'results',
      lastWinner: {
        playerUid: winnerPlayer.uid,
        playerName: winnerPlayer.name,
        cards: winningSubmission.cards,
        isGameWinner: winnerOfGame,
      }
    };
    
    if (winnerOfGame) {
      updateData.gameState = 'ended';
      logEvent(analytics, 'end_game', { lobby_id: lobbyId, winner_name: winnerPlayer.name });
    }

    await updateDoc(lobbyRef, updateData);
    
    logEvent(analytics, 'select_winner', {
      lobby_id: lobbyId,
      winner_uid: winnerUid,
      winner_name: winnerPlayer.name,
    });
  } catch (error) {
    console.error('Fehler beim Auswählen des Gewinners:', error);
    throw error;
  }
};

// Spieler kann einen Punkt gegen eine neue Hand tauschen
export const exchangeHandForPoint = async (lobbyId: string, playerId: string) => {
  try {
    const lobbyRef = doc(db, 'lobbies', lobbyId);
    const lobby = await getLobby(lobbyId);

    if (!lobby) throw new Error('Lobby nicht gefunden');

    const playerIndex = lobby.players.findIndex(p => p.uid === playerId);
    if (playerIndex === -1) throw new Error('Spieler nicht gefunden');
    
    const player = lobby.players[playerIndex];

    // Prüfungen
    if (player.score < 1) throw new Error('Nicht genügend Punkte für den Tausch.');
    if (player.hasExchangedHand) throw new Error('Du hast in dieser Runde bereits getauscht.');
    if (player.isCzar) throw new Error('Der Card Czar kann keine Karten tauschen.');

    const handSize = lobby.gameSettings?.handSize || 7;
    const oldHand = [...player.hand]; // Komplette Kartenobjekte der alten Hand

    // Sicherstellen, dass genügend neue Karten verfügbar sind
    let availableWhiteCards = [...lobby.availableWhiteCards];
    let discardPile = [...(lobby.discardPileWhite || [])];

    if (availableWhiteCards.length < handSize) {
      // Ablagestapel neu mischen, wenn nicht genügend Karten vorhanden sind
      console.log("Mische Ablagestapel für Kartentausch neu...");
      availableWhiteCards.push(...fisherYatesShuffle(discardPile));
      discardPile = [];
    }
    
    if (availableWhiteCards.length < handSize) {
      throw new Error('Nicht genügend neue Karten im gesamten Spiel verfügbar.');
    }

    // Neue Hand aus dem Stapel ziehen
    const newHand = availableWhiteCards.splice(0, handSize);
    
    // Spielerdaten aktualisieren
    const updatedPlayer = {
      ...player,
      score: player.score - 1,
      hand: newHand,
      hasExchangedHand: true,
    };

    const updatedPlayers = lobby.players.map(p => p.uid === playerId ? updatedPlayer : p);
    const newDiscardPile = [...discardPile, ...oldHand]; // Alte Hand zum Ablagestapel hinzufügen

    // Lobby-Dokument aktualisieren
    await updateDoc(lobbyRef, {
      players: updatedPlayers,
      availableWhiteCards: availableWhiteCards,
      discardPileWhite: newDiscardPile,
    });

  } catch (error) {
    console.error('Fehler beim Tauschen der Hand:', error);
    throw error;
  }
};

// Nächste Runde starten
export const startNextRound = async (lobbyId: string) => {
  try {
    const lobby = await getLobby(lobbyId);
    if (!lobby) throw new Error('Lobby nicht gefunden');

    // 1. Gespielte Karten der letzten Runde sammeln
    const playedWhiteCards = (lobby.submissions || []).flatMap(s => s.cards);
    
    // 2. Ablagestapel aktualisieren und dabei sicherstellen, dass er nur gültige Kartenobjekte enthält
    const sanitizedOldDiscardPile = (lobby.discardPileWhite || []).filter(
      card => card && typeof card === 'object' && card.id && card.text
    );
    const newDiscardPileWhite = [...sanitizedOldDiscardPile, ...playedWhiteCards];

    // 3. Verfügbaren Stapel bereinigen
    let availableWhiteCards = (lobby.availableWhiteCards || []).filter(
      availableCard => !playedWhiteCards.some(playedCard => playedCard.id === availableCard.id)
    );

    // 4. Neuen Card Czar bestimmen
    const currentCzarIndex = lobby.players.findIndex(p => p.isCzar);
    const nextCzarIndex = (currentCzarIndex + 1) % lobby.players.length;
    
    const updatedPlayers = lobby.players.map((player, index) => ({
      ...player,
      isCzar: index === nextCzarIndex,
      hasExchangedHand: false
    }));

    // 5. Neue schwarze Karte ziehen
    let availableBlackCards = lobby.availableBlackCards || [];
    if (availableBlackCards.length === 0) {
      // Wenn keine schwarzen Karten mehr da sind, Ablagestapel neu mischen
      availableBlackCards = fisherYatesShuffle([...(lobby.discardPileBlack || [])]);
    }
    const randomIndex = Math.floor(Math.random() * availableBlackCards.length);
    const newBlackCard = availableBlackCards.splice(randomIndex, 1)[0];

    // 6. Spielern neue Karten geben
    const handSize = lobby.gameSettings?.handSize || 7;
    const usedWhiteCardIds = playedWhiteCards.map(c => c.id); // IDs der gespielten Karten

    const { updatedPlayers: updatedPlayersWithNewCards, remainingAvailableCards, newDiscardPile } = await givePlayersNewCards(
      updatedPlayers, 
      availableWhiteCards,
      newDiscardPileWhite,
      usedWhiteCardIds, // IDs übergeben
      handSize
    );

    // 7. Lobby für nächste Runde aktualisieren
    const lobbyRef = doc(db, 'lobbies', lobbyId);
    const roundTime = lobby.gameSettings?.roundTimeSeconds || 90;
    const roundEndTime = lobby.gameSettings?.timerEnabled 
      ? new Date(Date.now() + roundTime * 1000) 
      : undefined;

    await updateDoc(lobbyRef, {
      players: updatedPlayersWithNewCards,
      currentBlackCard: newBlackCard,
      availableWhiteCards: remainingAvailableCards,
      availableBlackCards: availableBlackCards,
      discardPileWhite: newDiscardPile,
      discardPileBlack: arrayUnion(newBlackCard),
      roundNumber: lobby.roundNumber + 1,
      submissions: [],
      gamePhase: 'selecting',
      roundEndTime: roundEndTime,
      revealedSubmissionIndices: [],
      lastWinner: null,
      currentCzarUid: updatedPlayersWithNewCards.find(p => p.isCzar)?.uid,
    });

  } catch (error) {
    console.error('Fehler beim Starten der nächsten Runde:', error);
    throw error;
  }
};

// Hilfsfunktion: Spielern neue Karten geben
const givePlayersNewCards = async (players: Player[], availableCards: WhiteCard[], discardPile: WhiteCard[], usedCardIds: string[], targetHandSize: number = 7) => {
  let currentAvailableCards = [...availableCards];
  let currentDiscardPile = [...discardPile];

  const updatedPlayers = players.map(player => {
    if (player.isCzar) return player;

    // Zuerst die gespielten Karten aus der Hand entfernen
    const currentHand = player.hand.filter(card => !usedCardIds.includes(card.id));
    
    const cardsNeeded = targetHandSize - currentHand.length;
    if (cardsNeeded <= 0) {
      return { ...player, hand: currentHand };
    }

    // Wenn nicht genug Karten im Nachziehstapel sind, Ablagestapel neu mischen
    if (currentAvailableCards.length < cardsNeeded) {
      console.log("Mische Ablagestapel neu, da nicht genügend Karten vorhanden sind...");
      currentAvailableCards.push(...fisherYatesShuffle(currentDiscardPile));
      currentDiscardPile = [];
    }

    if (currentAvailableCards.length < cardsNeeded) {
      console.error("WARNUNG: Immer noch nicht genügend Karten nach dem Mischen!");
      // Nimm so viele Karten wie möglich
    }
    
    const newCards = currentAvailableCards.splice(0, cardsNeeded);
    
    return {
      ...player,
      hand: [...currentHand, ...newCards],
    };
  });
  
  return { updatedPlayers, remainingAvailableCards: currentAvailableCards, newDiscardPile: currentDiscardPile };
};

// Upvote für eine Einreichung
export const upvoteSubmission = async (lobbyId: string, submissionPlayerUid: string) => {
  try {
    const lobbyRef = doc(db, 'lobbies', lobbyId);
    const lobby = await getLobby(lobbyId);

    if (!lobby) throw new Error("Lobby nicht gefunden");

    // Sicherstellen, dass submissions ein Array ist
    const submissions = lobby.submissions || [];
    const submissionIndex = submissions.findIndex(s => s.playerUid === submissionPlayerUid);

    if (submissionIndex === -1) throw new Error("Einreichung nicht gefunden");

    // Neues submissions-Array mit dem aktualisierten Vote-Zähler erstellen
    const updatedSubmissions = submissions.map((submission, index) => {
      if (index === submissionIndex) {
        return {
          ...submission,
          votes: (submission.votes || 0) + 1,
        };
      }
      return submission;
    });

    // Das gesamte neue Array zurückschreiben, um Datenkorruption zu verhindern
    await updateDoc(lobbyRef, {
      submissions: updatedSubmissions,
      lastActivity: new Date(),
    });
  } catch (error) {
    console.error("Fehler beim Upvoten:", error);
    throw error;
  }
};

export const kickPlayer = async (lobbyId: string, hostUid: string, playerToKickUid: string) => {
  try {
    const lobbyRef = doc(db, 'lobbies', lobbyId);
    const lobby = await getLobby(lobbyId);

    if (!lobby) throw new Error("Lobby nicht gefunden");
    if (lobby.hostUid !== hostUid) throw new Error("Nur der Host kann Spieler entfernen.");
    if (lobby.hostUid === playerToKickUid) throw new Error("Der Host kann sich nicht selbst kicken.");

    const updatedPlayers = lobby.players.filter(p => p.uid !== playerToKickUid);
    const updatedPlayerUids = lobby.playerUids.filter(uid => uid !== playerToKickUid);

    await updateDoc(lobbyRef, {
      players: updatedPlayers,
      playerUids: updatedPlayerUids,
      lastActivity: new Date(),
    });
  } catch (error) {
    console.error("Fehler beim Kicken des Spielers:", error);
    throw error;
  }
};

export const getCategories = async (): Promise<CardCategory[]> => {
  const categoriesRef = collection(db, COLLECTIONS.CARD_CATEGORIES);
  const q = query(categoriesRef, where("isActive", "==", true));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CardCategory));
};

/**
 * Echter Fisher-Yates-Shuffle-Algorithmus für mathematisch korrekte Randomisierung
 * @param array Das zu mischende Array (wird in-place modifiziert)
 * @returns Das gemischte Array
 */
function fisherYatesShuffle<T>(array: T[]): T[] {
  const shuffled = [...array]; // Kopie erstellen um Original nicht zu ändern
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
